import React, { useState, useRef, useEffect, useLayoutEffect } from 'react'
import { createPortal } from 'react-dom'
import styled from 'styled-components'

const Container = styled.div`
  position: relative;
  display: inline-block;
`

const Trigger = styled.div`
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
`

const Menu = styled.div`
  position: absolute;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radii.md};
  box-shadow: ${({ theme }) => theme.shadow.lg};
  min-width: 200px;
  z-index: 99999; /* Increased significantly to be above modals (observed as 10900) */
  overflow: hidden;
  padding: 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const TransparentOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 99998; /* Increased significantly to be above modals */
  cursor: default;
`

export function Dropdown({ trigger, children }) {
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef(null)
  const [coords, setCoords] = useState({ top: 0, left: 0 })

  useLayoutEffect(() => {
    if (isOpen && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect()
      const scrollY = window.scrollY
      const scrollX = window.scrollX
      const viewportWidth = document.documentElement.clientWidth
      
      // Initial positioning: align left with trigger, below trigger
      let top = rect.bottom + scrollY + 8
      let left = rect.left + scrollX
      let right = 'auto' // Reset right

      // Check if menu goes offscreen to the right (assuming min-width 200px or actual width)
      // Since we don't know the exact width of children yet, we might guess or use a ref for the menu.
      // But for now, let's assume if it's too close to the right edge, align right.
      
      const estimatedWidth = 220 // Approximate width
      if (rect.left + estimatedWidth > viewportWidth) {
          // Align right with trigger
          left = 'auto'
          // Distance from right edge of viewport to right edge of trigger
          // We want the menu's right edge to align with trigger's right edge?
          // Or just stick to the right side of the screen?
          // Let's align right edge of menu with right edge of trigger
          // right = viewportWidth - rect.right (distance from right)
          // But Portal is document.body, so 'right' style works relative to viewport width if body is 100%
          
          // Let's use left positioning but calculated to align right
          left = rect.right + scrollX - estimatedWidth 
          
          // Actually, let's try the previous logic but inverted?
          // Previous logic was: right = document.documentElement.clientWidth - rect.right
          // which aligns the right side of the menu with the right side of the trigger.
          
          // If we want to prevent overflow left, we ensure left >= 0.
          if (left < 0) left = 10
      }

      // Check if it goes offscreen to the left
      if (left < 0) left = 10

      setCoords({ top, left, right })
    }
  }, [isOpen])

  // Standard click outside is still useful for clicks within the same document
  useEffect(() => {
    function handleClickOutside(event) {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        // We rely on TransparentOverlay for outside clicks mostly, 
        // but this handles cases where the click might bubble up differently?
        // Actually with Portal, event bubbling acts as if it's in the React tree.
        // So clicking Overlay triggers setIsOpen(false).
        // Clicking Trigger triggers setIsOpen(!isOpen).
        // We just need to ensure we don't double toggle.
      }
    }
    // document.addEventListener('mousedown', handleClickOutside)
    // return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  return (
    <Container ref={containerRef}>
      <Trigger onClick={() => setIsOpen(!isOpen)}>
        {trigger}
      </Trigger>
      {isOpen && createPortal(
        <>
          <TransparentOverlay onClick={() => setIsOpen(false)} />
          <Menu 
            style={{ top: coords.top, left: coords.left }}
            onClick={() => setIsOpen(false)}
          >
            {children}
          </Menu>
        </>,
        document.body
      )}
    </Container>
  )
}
